// Ch78 · THE CLAIM INBOX — the top of the Leads page's left column.
//
// The row anatomy is the dashboard's New Leads feed, which Grant already
// likes: [⊕] [name · what they want] [$budget · how long they have waited].
// What is NOT borrowed is that card's chrome — its subheader prose, its
// per-row "revenue opportunity uses stated budget, timing…" sentence, and its
// claim that rows are "highest revenue opportunity first". On this page they
// are not: they are NEWEST FIRST, biggest budget breaking ties, which is what
// Grant asked for and what a person triaging an inbox actually wants. A card
// that states an ordering its rows do not have is worse than one that states
// nothing, so this one states the ordering it really uses, in the (i).
//
// The act itself is the estate's canonical one: runLeadAction(id, "convert"),
// same RPC, same ownership-conflict message as the dashboard.

import React, { useMemo, useRef, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoDot from "./InfoDot";
import { TOUCH_TARGET } from "../../theme/theme";
import { buildLeadDisplayName, buildLeadLocationParts } from "../../lib/leadPreferences";
import { LEAD_TEXTS_CHIP_LABEL } from "../../lib/leadSmsOptIn";
import { compactMoney } from "../../lib/moveInBoard";
import { TIMELINE_LANES, laneIndex, toneColor } from "../../lib/leadsTimeline";
import { runLeadAction } from "../../services/leadConversionService";
import { leadActionErrorMessage } from "../../lib/leadRefusal";
import { OVERDUE_HOURS, type WorkRow } from "../../lib/leadsWork";

export interface LeadClaimInboxProps {
  /** The work list's `unclaimed` rows — one definition, two renderings. */
  rows: WorkRow[];
  /** The claim ledger could not be read; refuse to list rather than guess. */
  unavailable: boolean;
  onOpen: (row: WorkRow) => void;
  /** A conversion landed; the owner of the data must refetch. */
  onResolved: () => void;
  /** "View all N →" — takes the operator to the full work list. */
  onViewAll: () => void;
  max?: number;
}

const INBOX_INFO =
  "Every lead nobody has picked up yet, newest first with the biggest budget breaking ties — the same " +
  "“Unclaimed” group the work list below shows, read from the same claim ledger. ⊕ converts the lead " +
  "into a client, which is what makes them contactable at all. A row past " +
  `${OVERDUE_HOURS}h is red.`;

const DEFAULT_MAX = 6;

export default function LeadClaimInbox({
  rows,
  unavailable,
  onOpen,
  onResolved,
  onViewAll,
  max = DEFAULT_MAX,
}: LeadClaimInboxProps) {
  const theme = useTheme();
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const inFlight = useRef<Set<string>>(new Set());

  // Grant's ordering, stated and then actually used: newest first, biggest
  // budget on ties. Not the work list's cost-of-waiting — an inbox is read
  // top-down as "what just came in".
  const ordered = useMemo(
    () =>
      [...rows].sort(
        (a, b) =>
          (a.waitingMs ?? Number.MAX_SAFE_INTEGER) - (b.waitingMs ?? Number.MAX_SAFE_INTEGER) ||
          (b.person.budget ?? 0) - (a.person.budget ?? 0),
      ),
    [rows],
  );
  const overdue = ordered.filter((r) => r.overdue).length;
  const shown = ordered.slice(0, max);

  const convert = async (row: WorkRow) => {
    const lead = row.person.lead;
    if (!lead || inFlight.current.has(lead.id)) return;
    inFlight.current.add(lead.id);
    setPending((p) => ({ ...p, [lead.id]: true }));
    setErrors((e) => ({ ...e, [lead.id]: "" }));
    try {
      await runLeadAction(lead.id, "convert");
      onResolved();
    } catch (error) {
      // EVENT-2179 · the `LeadConflictError` arm cannot fire today (see
      // leadRefusal.ts) and every other refusal fell through to `error.message`,
      // which for an ApiError is only `API error: <status>`. The helper keeps both
      // conflict arms and maps this route's real codes.
      setErrors((e) => ({
        ...e,
        [lead.id]: leadActionErrorMessage(error, buildLeadDisplayName(lead)),
      }));
    } finally {
      inFlight.current.delete(lead.id);
      setPending((p) => {
        const next = { ...p };
        delete next[lead.id];
        return next;
      });
    }
  };

  const caption = (row: WorkRow): string => {
    const lead = row.person.lead;
    const areas = lead ? buildLeadLocationParts(lead.neighborhoods, lead.neighborhood_other) : [];
    return [
      areas.length ? areas.slice(0, 2).join(", ") : null,
      row.person.dateMs !== null
        ? `move-in ${new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(row.person.dateMs))}`
        : row.person.moveInText
          ? `move-in “${row.person.moveInText}”`
          : null,
      row.sourceLabel ? `from ${row.sourceLabel}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }} data-testid="leads-claim-inbox">
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={800}>Claim inbox</Typography>
        {overdue > 0 && (
          <Chip size="small" color="error" variant="outlined" label={`${overdue} over ${OVERDUE_HOURS}h`} data-testid="inbox-overdue" />
        )}
        <InfoDot text={INBOX_INFO} testId="inbox-info" />
      </Stack>

      {unavailable ? (
        <Typography variant="body2" color="warning.main" data-testid="inbox-unavailable">
          The claim ledger could not be read, so this inbox cannot say who is unclaimed. Refresh to retry.
        </Typography>
      ) : shown.length === 0 ? (
        <Typography variant="body2" color="text.secondary" data-testid="inbox-empty">
          Nobody is waiting to be picked up.
        </Typography>
      ) : (
        <Stack data-testid="inbox-rows">
          {shown.map((row) => {
            const lead = row.person.lead;
            const name = lead ? buildLeadDisplayName(lead) : row.person.name;
            const busy = Boolean(lead && pending[lead.id]);
            const error = lead ? errors[lead.id] : "";
            return (
              <Box key={row.key} sx={{ borderTop: `1px solid ${theme.palette.divider}`, "&:first-of-type": { borderTop: 0 } }}>
                <Stack direction="row" alignItems="center" spacing={1.25} sx={{ py: 0.75 }}>
                  <Tooltip title="Convert to client" placement="left">
                    <span>
                      <IconButton
                        size="small"
                        aria-label={`Convert ${name} to client`}
                        data-testid={`inbox-convert-${row.key}`}
                        disabled={busy}
                        onClick={() => void convert(row)}
                        sx={{
                          width: TOUCH_TARGET,
                          height: TOUCH_TARGET,
                          flex: "none",
                          color: "primary.main",
                          border: `1px dashed ${alpha(theme.palette.primary.main, 0.45)}`,
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.14), borderStyle: "solid" },
                        }}
                      >
                        {busy ? <CircularProgress size={15} aria-label="Converting lead" /> : <AddIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  <ButtonBase
                    onClick={() => onOpen(row)}
                    aria-label={`Open ${name}`}
                    data-testid={`inbox-open-${row.key}`}
                    sx={{ flex: 1, minWidth: 0, textAlign: "left", display: "block", py: 0.25 }}
                  >
                    <Typography variant="body2" fontWeight={700} noWrap>{name}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                      {caption(row)}
                    </Typography>
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={TIMELINE_LANES[laneIndex(row.person.tier)]?.label ?? "Not scored"}
                        sx={{ height: 20, fontSize: 11, borderColor: alpha(toneColor(theme, row.person.tone), 0.5), color: toneColor(theme, row.person.tone) }}
                      />
                      {row.person.textsOk && (
                        <Chip size="small" variant="outlined" color="primary" label={LEAD_TEXTS_CHIP_LABEL} sx={{ height: 20, fontSize: 11 }} />
                      )}
                    </Stack>
                  </ButtonBase>

                  <Box sx={{ textAlign: "right", flex: "none" }}>
                    <Typography variant="body2" fontWeight={700} color="primary.main">
                      {row.person.budget === null ? "—" : compactMoney(row.person.budget)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: row.overdue ? 800 : 500, color: row.overdue ? "error.main" : "text.secondary" }}
                    >
                      {row.waitingLabel === "—" ? "—" : `${row.waitingLabel} ago`}
                    </Typography>
                  </Box>
                </Stack>
                {error && (
                  <Typography variant="caption" color="error.main" sx={{ display: "block", pb: 0.75 }} data-testid={`inbox-error-${row.key}`}>
                    {error}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Stack>
      )}

      {!unavailable && ordered.length > shown.length && (
        <Box
          component="button"
          onClick={onViewAll}
          data-testid="inbox-view-all"
          sx={{ border: "none", bgcolor: "transparent", color: "primary.main", fontWeight: 700, fontSize: 12.5, cursor: "pointer", p: 0, mt: 1, fontFamily: "inherit" }}
        >
          View all {ordered.length} →
        </Box>
      )}
    </Paper>
  );
}
